import { cn } from "cn";
import { LoaderCircle } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { osName } from "react-device-detect";
import { Button } from "@/components/ui/button/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type SendButtonProps = {
  onClick: () => void | Promise<void>;
  isLoading: boolean;
  disabled?: boolean;
  /** Visible button label (default: Send) */
  label?: string;
  /** First line in tooltip, above keyboard hints */
  tooltipDescription?: string;
  /** Render an icon-only button instead of the labelled one. */
  icon?: ReactNode;
  /** Button size — defaults to the standard button size; pass "icon-md" with `icon`. */
  size?: ComponentProps<typeof Button>["size"];
  /** Extra classes on the button — the edit composer's green Save confirm. */
  className?: string;
  "data-test"?: string;
};

export function SendButton({
  onClick,
  isLoading,
  disabled = false,
  label = "Send",
  tooltipDescription = "Send message",
  icon,
  size,
  className,
  ...props
}: SendButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          {...props}
          size={size}
          className={className}
          // In icon mode the label is never rendered, so without this the button
          // has no accessible name at all.
          aria-label={icon ? label : undefined}
          onClick={() => void onClick()}
          disabled={disabled || isLoading}
        >
          {icon ? (
            isLoading ? (
              <LoaderCircle className="animate-spin" />
            ) : (
              icon
            )
          ) : (
            <>
              {isLoading && <LoaderCircle className="animate-spin" />}
              {label}
            </>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <div
          className={cn(
            /* sorted */
            "flex items-center gap-2",
            "py-1",
            "text-text-light text-caption",
          )}
        >
          {tooltipDescription}
          <div className="flex items-center gap-1">
            <div
              className={cn(
                /* sorted */
                "flex items-center justify-center",
                "size-5",
                "ml-1",
                "text-xs text-text-light",
                "border border-solid rounded-md border-border-light",
              )}
            >
              {osName === "Mac OS" ? "⌘" : "Ctrl"}
            </div>
            <div
              className={cn(
                /* sorted */
                "flex items-center justify-center",
                "h-5",
                "px-px",
                "text-xs text-text-light",
                "border border-solid rounded-md border-border-light",
              )}
            >
              Enter
            </div>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
